import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { Trash2, Users, Infinity, Plus } from "lucide-react";
import { dataService } from "../../Services/data/data.service";
import ErrorHandlerService from "../../Services/ErrorHandlerService";
import Button from "./Buttons/Button";
import IconButton from "./Buttons/IconButton";
import TextInput from "./TextInput";
import Chip from "./ui/Chip";
import Tooltip from "./Tooltip";
import ConfirmDialog from "./Dialogs/ConfirmDialog";
import { color, radius, fontFamily } from "./tokens";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// New accounts get a random throwaway password; the user sets their own via the password reset email
const generateTemporaryPassword = () => {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("") + "!Aa1";
};

/**
 * Lists, adds and removes the administrators of a subscriber. Changes are saved immediately.
 * Every administrator occupies a user seat, so adding one is limited by the subscription user limit
 * (the backend enforces this, the component only shows the current usage).
 *
 * @param {string}   subscriberUniqueId
 * @param {number}   currentUserId      - used to mark (and prevent removing) yourself
 * @param {Function} [onAdminsChanged]  - called with the backend response after every add/remove
 */
export default function SubscriberAdminsManager({ subscriberUniqueId, currentUserId, onAdminsChanged }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [userLimit, setUserLimit] = useState(null);
  const [generalError, setGeneralError] = useState(null);

  // Add form
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [requiresNewUser, setRequiresNewUser] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState("");
  const [lastNameError, setLastNameError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Remove confirmation
  const [adminPendingRemoval, setAdminPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  // Keeps the confirm text intact while the confirm dialog fades out after adminPendingRemoval is cleared
  const lastRemovalEmail = useRef("");
  if (adminPendingRemoval) lastRemovalEmail.current = adminPendingRemoval.emailAddress;

  useEffect(() => {
    if (!subscriberUniqueId) return;

    resetForm();
    setGeneralError(null);
    setAdminPendingRemoval(null);
    setLoading(true);

    dataService
      .getSubscriberAdmins(subscriberUniqueId)
      .then((response) => {
        if (response && response.success) {
          applyResponse(response);
        } else {
          setGeneralError(getErrorMessage(response, "subscriberAdmins.loadError"));
        }
      })
      .catch((error) => {
        ErrorHandlerService.handleApiError(error, t("subscriberAdmins.loadError"));
      })
      .finally(() => setLoading(false));
  }, [subscriberUniqueId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Translate a failed backend response via its errorCode; fall back to the backend's English message
  const getErrorMessage = (response, fallbackKey) =>
    response?.errorCode
      ? t(`subscriberAdmins.errors.${response.errorCode}`, { limit: response.userLimit, defaultValue: response.message || t(fallbackKey) })
      : response?.message || t(fallbackKey);

  const applyResponse = (response) => {
    setAdmins(response.administrators || []);
    setUserCount(response.userCount);
    setUserLimit(response.userLimit);
  };

  const resetForm = () => {
    setEmail("");
    setEmailError(null);
    setRequiresNewUser(false);
    setFirstName("");
    setFirstNameError(null);
    setLastName("");
    setLastNameError(null);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError(null);
    // A different email may belong to an existing user, so ask the backend again
    setRequiresNewUser(false);
  };

  const handleAddSuccess = (response) => {
    applyResponse(response);
    resetForm();
    ErrorHandlerService.showSuccess(t("subscriberAdmins.addSuccess"));
    onAdminsChanged && onAdminsChanged(response);
  };

  // Creates the Firebase account on a secondary app so the signed-in admin keeps their session.
  // Returns { firebaseId, created } or null when the email was rejected.
  const createFirebaseUser = async (emailAddress) => {
    const auth = getAuth();
    const secondaryApp = initializeApp(auth.app.options, "secondaryAppForSubscriberAdminCreation");
    const secondaryAuth = getAuth(secondaryApp);

    try {
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, emailAddress, generateTemporaryPassword());
      await secondaryAuth.signOut();
      return { firebaseId: userCredential.user.uid, created: true };
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        // Firebase account exists without a Taplr user; login matches on email, so continue without an id
        return { firebaseId: null, created: false };
      }
      if (error.code === "auth/invalid-email") {
        setEmailError(t("subscriberAdmins.emailInvalid"));
        return null;
      }
      throw error;
    } finally {
      try {
        await deleteApp(secondaryApp);
      } catch (_) {}
    }
  };

  const handleAdd = async () => {
    setGeneralError(null);
    const emailAddress = email.trim();

    if (!EMAIL_REGEX.test(emailAddress)) {
      setEmailError(t("subscriberAdmins.emailInvalid"));
      return;
    }

    if (requiresNewUser) {
      let valid = true;
      if (!firstName.trim()) {
        setFirstNameError(t("common.required"));
        valid = false;
      }
      if (!lastName.trim()) {
        setLastNameError(t("common.required"));
        valid = false;
      }
      if (!valid) return;
    }

    setSubmitting(true);
    try {
      if (!requiresNewUser) {
        const response = await dataService.addSubscriberAdmin({ subscriberUniqueId, emailAddress });
        if (response.success) {
          handleAddSuccess(response);
        } else if (response.requiresNewUser) {
          setRequiresNewUser(true);
        } else {
          setGeneralError(getErrorMessage(response, "subscriberAdmins.addError"));
        }
        return;
      }

      const firebaseResult = await createFirebaseUser(emailAddress);
      if (!firebaseResult) return;

      const response = await dataService.addSubscriberAdmin({
        subscriberUniqueId,
        emailAddress,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        firebaseId: firebaseResult.firebaseId,
      });

      if (!response.success) {
        setGeneralError(getErrorMessage(response, "subscriberAdmins.addError"));
        return;
      }

      if (firebaseResult.created) {
        await sendPasswordResetEmail(getAuth(), emailAddress);
      }
      handleAddSuccess(response);
    } catch (error) {
      ErrorHandlerService.handleApiError(error, t("subscriberAdmins.addError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!adminPendingRemoval) return;

    setGeneralError(null);
    setRemoving(true);
    try {
      const response = await dataService.removeSubscriberAdmin({ subscriberUniqueId, userId: adminPendingRemoval.userId });
      if (response.success) {
        applyResponse(response);
        ErrorHandlerService.showSuccess(t("subscriberAdmins.removeSuccess"));
        onAdminsChanged && onAdminsChanged(response);
      } else {
        setGeneralError(getErrorMessage(response, "subscriberAdmins.removeError"));
      }
    } catch (error) {
      ErrorHandlerService.handleApiError(error, t("subscriberAdmins.removeError"));
    } finally {
      setRemoving(false);
      setAdminPendingRemoval(null);
    }
  };

  const getRemoveBlockedReason = (admin) => {
    if (admin.userId === currentUserId) return t("subscriberAdmins.cannotRemoveSelf");
    if (admins.length <= 1) return t("subscriberAdmins.cannotRemoveLast");
    return null;
  };

  const userLimitReached = userLimit !== null && userLimit !== undefined && userCount >= userLimit;

  return (
    <div className={classes.content}>
      <div className={classes.header}>
        <span className={classes.sectionLabel}>
          {t("subscriberAdmins.title")} ({admins.length})
        </span>
        <Chip variant={userLimitReached ? "warning" : "default"} size="small" startAdornment={<Users />}>
          <span className={classes.seatCount}>
            {userCount}/{userLimit ?? <Infinity size={14} />}
          </span>
        </Chip>
      </div>
      <p className={classes.hint}>{t("subscriberAdmins.seatHint")}</p>

      {generalError && (
        <div className={classes.errorContainer}>
          <p className={classes.errorMessage}>{generalError}</p>
        </div>
      )}

      <div className={classes.list}>
        {loading && <p className={classes.listPlaceholder}>{t("common.loading")}</p>}
        {!loading &&
          admins.map((admin) => {
            const blockedReason = getRemoveBlockedReason(admin);
            const removeButton = (
              <IconButton
                size="small"
                ariaLabel={t("subscriberAdmins.removeAriaLabel", { email: admin.emailAddress })}
                disabled={!!blockedReason || removing}
                onClick={() => setAdminPendingRemoval(admin)}
              >
                <Trash2 />
              </IconButton>
            );

            return (
              <div key={admin.userId} className={classes.row}>
                <div className={classes.rowText}>
                  <span className={classes.rowName}>
                    {admin.firstname} {admin.name}
                  </span>
                  <span className={classes.rowEmail}>{admin.emailAddress}</span>
                </div>
                <div className={classes.rowEnd}>
                  {admin.userId === currentUserId && (
                    <Chip variant="info" size="extraSmall">
                      {t("subscriberAdmins.you")}
                    </Chip>
                  )}
                  {admin.isCompanyUser && (
                    <Chip variant="light" size="extraSmall">
                      {t("subscriberAdmins.companyUser")}
                    </Chip>
                  )}
                  {blockedReason ? <Tooltip content={blockedReason}>{removeButton}</Tooltip> : removeButton}
                </div>
              </div>
            );
          })}
      </div>

      <ConfirmDialog
        open={!!adminPendingRemoval}
        title={t("subscriberAdmins.removeTitle")}
        message={t("subscriberAdmins.removeConfirm", { email: lastRemovalEmail.current })}
        confirmLabel={t("subscriberAdmins.remove")}
        onConfirm={handleConfirmRemove}
        onCancel={() => setAdminPendingRemoval(null)}
        destructive
        loading={removing}
      />

      <div className={classes.addRow}>
        <TextInput
          fullWidth
          type="email"
          placeholder={t("subscriberAdmins.emailPlaceholder")}
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          onKeyDown={(e) => e.key === "Enter" && !requiresNewUser && handleAdd()}
          inputProps={{ maxLength: 100, "aria-label": t("subscriberAdmins.addTitle") }}
        />
        {!requiresNewUser && (
          <Button startIcon={<Plus />} onClick={handleAdd} loading={submitting} disabled={!email.trim() || loading}>
            {t("subscriberAdmins.add")}
          </Button>
        )}
      </div>

      {requiresNewUser && (
        <div className={classes.newUserSection}>
          <p className={classes.hint}>{t("subscriberAdmins.newUserHint")}</p>
          <div className={classes.nameRow}>
            <TextInput
              fullWidth
              label={t("subscriberAdmins.firstName")}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFirstNameError(null);
              }}
              error={!!firstNameError}
              helperText={firstNameError}
              inputProps={{ maxLength: 50 }}
              autoFocus
            />
            <TextInput
              fullWidth
              label={t("subscriberAdmins.lastName")}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setLastNameError(null);
              }}
              error={!!lastNameError}
              helperText={lastNameError}
              inputProps={{ maxLength: 50 }}
            />
          </div>
          <div className={classes.confirmActions}>
            <Button size="small" variant="light" onClick={resetForm} disabled={submitting}>
              {t("common.cancel")}
            </Button>
            <Button size="small" startIcon={<Plus />} onClick={handleAdd} loading={submitting}>
              {t("subscriberAdmins.createAndAdd")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    fontFamily: fontFamily.base,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  sectionLabel: {
    fontSize: 16,
    color: color.text.primary,
  },
  seatCount: {
    display: "inline-flex",
    alignItems: "center",
  },
  hint: {
    margin: 0,
    fontSize: 13,
    color: color.text.secondary,
  },
  errorContainer: {
    backgroundColor: "#FDF3F3",
    border: "1px solid #FBE5E5",
    borderRadius: radius.md,
    padding: 12,
  },
  errorMessage: {
    margin: 0,
    fontSize: 14,
    color: color.status.error,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
  },
  listPlaceholder: {
    margin: 0,
    padding: "12px",
    fontSize: 14,
    color: color.text.secondary,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "8px 12px",
    borderBottom: `1px solid ${color.border.default}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  rowText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  rowName: {
    fontSize: 14,
    color: color.text.primary,
  },
  rowEmail: {
    fontSize: 13,
    color: color.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowEnd: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  confirmActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  addRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  newUserSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  nameRow: {
    display: "flex",
    gap: 8,
  },
}));
